import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'store/hook';
import { getTpmsPromises } from 'handlers/util/fetch.all';

import TirePressureHandler from 'handlers/tire-pressure';
import TagThresholdHandler from 'handlers/tag.threshold.handler';
import TirePatternHandler from 'handlers/tire-patterm.handler';

export const useTpmsInit = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const customer = useAppSelector((state) => state.authReducer.customer);
  const customerId = customer.id;
  const isTiresModule = customer.modules?.isTiresModule ?? false;
  const isAuthenticated = useAppSelector(
    (state) => state.authReducer.isAuthenticated
  );

  const fetchedCustomerIdRef = useRef<string | null>(null);

  // Reset when customer changes
  useEffect(() => {
    if (
      fetchedCustomerIdRef.current &&
      fetchedCustomerIdRef.current !== customerId
    ) {
      fetchedCustomerIdRef.current = null;
    }
  }, [customerId]);

  useEffect(() => {
    const TPMS_PATHS = [
      '/tire-insights',
      '/tire-analytics',
      '/tire-management',
      '/settings/tpms-sensors',
    ];

    const isTpmsRoute = TPMS_PATHS.some((path) =>
      location.pathname.startsWith(path)
    );

    if (!isAuthenticated || !customerId || !isTiresModule || !isTpmsRoute) {
      setLoading(false);
      return;
    }

    if (fetchedCustomerIdRef.current === customerId) {
      setLoading(false);
      return;
    }

    const fetchTpmsData = async () => {
      fetchedCustomerIdRef.current = customerId;
      setLoading(true);

      try {
        const handlers = {
          tirePressureHandler: new TirePressureHandler(),
          tagThresholdHandler: new TagThresholdHandler(),
          tirePatternHandler: new TirePatternHandler(),
        };

        const promises = getTpmsPromises(handlers as any);

        await Promise.allSettled(promises);
      } catch (error) {
        console.error('Error fetching TPMS data:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchTpmsData();
  }, [location.pathname, customerId, isAuthenticated, isTiresModule]);

  return { loading };
};
